import type { Event as EventModel } from '../api/types';

/** YYYYMMDDTHHMMSSZ — UTC format used by both iCalendar and Google Calendar. */
function formatIcsDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    date.getUTCFullYear().toString() +
    pad(date.getUTCMonth() + 1) +
    pad(date.getUTCDate()) +
    'T' +
    pad(date.getUTCHours()) +
    pad(date.getUTCMinutes()) +
    pad(date.getUTCSeconds()) +
    'Z'
  );
}

function escapeIcsText(text: string): string {
  return text.replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/,/g, '\\,').replace(/;/g, '\\;');
}

/** Generate and trigger download of an .ics file for the given event. */
export function downloadIcs(evt: EventModel): void {
  const start = new Date(evt.starts_at);
  const end = evt.ends_at ? new Date(evt.ends_at) : new Date(start.getTime() + 60 * 60 * 1000);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Stututu//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:event-${evt.id}@stututu.local`,
    `DTSTAMP:${formatIcsDate(new Date())}`,
    `DTSTART:${formatIcsDate(start)}`,
    `DTEND:${formatIcsDate(end)}`,
    `SUMMARY:${escapeIcsText(evt.title)}`,
    evt.description ? `DESCRIPTION:${escapeIcsText(evt.description)}` : '',
    `LOCATION:${escapeIcsText([evt.location_name, evt.city, evt.country].filter(Boolean).join(', '))}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter(Boolean);

  const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${evt.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Build a Google Calendar "Add event" URL for the given event. */
export function googleCalendarUrl(evt: EventModel): string {
  const start = new Date(evt.starts_at);
  const end = evt.ends_at ? new Date(evt.ends_at) : new Date(start.getTime() + 60 * 60 * 1000);
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: evt.title,
    dates: `${formatIcsDate(start)}/${formatIcsDate(end)}`,
    details: evt.description ?? '',
    location: [evt.location_name, evt.city, evt.country].filter(Boolean).join(', '),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}
